import React from 'react';
import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { useScrollReveal } from '../hooks/useScrollReveal';
import heroMeeting from '../assets/hero-meeting.jpg';
import heroSigning from '../assets/hero-signing.jpg';

const About = () => {
  useScrollReveal();

  return (
    <>
      <Nav darkMode={true} />
      
      <section className="hero-dark">
        <div className="grid-bg"></div>
        <div className="hero-dark-inner">
          <div className="pill reveal in">
            <span>✦</span> Founded in 2019
          </div>
          <h1 className="reveal in">We're building the future of government technology.</h1>
          <p className="sub reveal in">
            Valor Technyx exists to help public-sector teams deliver better services faster—without sacrificing security, compliance, or accountability.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="intro-line">
        <div className="wrap reveal">
          <p>
            We believe government software should be as modern, intuitive, and reliable as the best consumer products—while meeting the unique demands of public service.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section>
        <div className="wrap">
          <div className="about-story reveal">
            <div className="about-story-content">
              <span className="eyebrow">Our Story</span>
              <h2>Born from frustration with legacy systems.</h2>
              <p>
                Valor Technyx was founded by a team of engineers and public policy experts who spent years watching talented civil servants struggle with outdated, inflexible software. We saw agencies forced to choose between speed and compliance, between user experience and security.
              </p>
              <p>
                We knew there had to be a better way. So we built it.
              </p>
              <p>
                Today, Valor powers case management, program delivery, and constituent services for agencies across federal, state, and local government—helping them modernize without the usual pain, risk, or multi-year timelines.
              </p>
            </div>
            <div className="about-story-image">
              <img src={heroMeeting} alt="Team collaboration" />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="fog-section">
        <div className="wrap">
          <div className="section-head reveal">
            <span className="eyebrow">Our Values</span>
            <h2>What drives us every day.</h2>
          </div>
          <div className="values-grid reveal-stagger">
            <div className="value-card">
              <div className="value-number">01</div>
              <h3>Public service first</h3>
              <p>
                We're not just building software—we're supporting the people who serve their communities. Every feature, every decision starts with the question: does this help agencies deliver better outcomes for constituents?
              </p>
            </div>
            <div className="value-card">
              <div className="value-number">02</div>
              <h3>Security by design</h3>
              <p>
                Government data is sensitive. We build with FedRAMP, NIST 800-53, and state-level frameworks in mind from day one—not as an afterthought. Compliance shouldn't slow you down.
              </p>
            </div>
            <div className="value-card">
              <div className="value-number">03</div>
              <h3>Transparency & accountability</h3>
              <p>
                Public-sector work demands trust. We provide full audit trails, clear documentation, and honest communication about what our platform can and can't do.
              </p>
            </div>
            <div className="value-card">
              <div className="value-number">04</div>
              <h3>Pragmatic innovation</h3>
              <p>
                We don't chase trends. We focus on proven, practical technology that solves real problems—and we make sure it integrates with the systems agencies already run.
              </p>
            </div>
            <div className="value-card">
              <div className="value-number">05</div>
              <h3>Long-term partnership</h3>
              <p>
                Government projects don't end at launch. We're committed to supporting our customers through implementation, training, ongoing updates, and evolving requirements.
              </p>
            </div>
            <div className="value-card">
              <div className="value-number">06</div>
              <h3>Accessibility for all</h3>
              <p>
                Public services should be accessible to everyone. We build with WCAG standards, plain language, and inclusive design principles at the core.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section>
        <div className="wrap">
          <div className="section-head reveal">
            <span className="eyebrow">Leadership</span>
            <h2>Meet the team behind Valor.</h2>
            <p>A diverse group of technologists, policy experts, and public service veterans.</p>
          </div>
          <div className="team-grid reveal-stagger">
            <div className="team-card">
              <div className="team-avatar">
                <div className="team-initials">SK</div>
              </div>
              <h3>Sarah Kim</h3>
              <p className="team-role">Co-Founder & CEO</p>
              <p className="team-bio">
                Former Chief Digital Officer at a state health agency. 12 years modernizing public-sector technology.
              </p>
            </div>
            <div className="team-card">
              <div className="team-avatar">
                <div className="team-initials">MR</div>
              </div>
              <h3>Marcus Rodriguez</h3>
              <p className="team-role">Co-Founder & CTO</p>
              <p className="team-bio">
                Ex-Google engineer. Built compliance infrastructure for federal contractors. MIT Computer Science.
              </p>
            </div>
            <div className="team-card">
              <div className="team-avatar">
                <div className="team-initials">JP</div>
              </div>
              <h3>Dr. Jennifer Park</h3>
              <p className="team-role">VP of Product</p>
              <p className="team-bio">
                PhD in Public Policy. Led digital transformation at USDS. Expert in human-centered government services.
              </p>
            </div>
            <div className="team-card">
              <div className="team-avatar">
                <div className="team-initials">DW</div>
              </div>
              <h3>David Washington</h3>
              <p className="team-role">VP of Engineering</p>
              <p className="team-bio">
                20+ years building secure, scalable systems. Former architect at defense contractors and civic tech startups.
              </p>
            </div>
            <div className="team-card">
              <div className="team-avatar">
                <div className="team-initials">LC</div>
              </div>
              <h3>Lisa Chen</h3>
              <p className="team-role">Head of Compliance</p>
              <p className="team-bio">
                FedRAMP auditor turned product leader. Ensures every Valor deployment meets the highest security standards.
              </p>
            </div>
            <div className="team-card">
              <div className="team-avatar">
                <div className="team-initials">TA</div>
              </div>
              <h3>Thomas Anderson</h3>
              <p className="team-role">Head of Customer Success</p>
              <p className="team-bio">
                Former program manager at municipal government. Dedicated to ensuring agencies get maximum value from Valor.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="fog-section">
        <div className="wrap">
          <div className="section-head reveal">
            <span className="eyebrow">Our Impact</span>
            <h2>Making a difference in public service.</h2>
          </div>
          <div className="stats reveal-stagger">
            <div className="stat">
              <h3 data-count="42">0</h3>
              <p>Government agencies served</p>
            </div>
            <div className="stat">
              <h3 data-count="2.4" data-suffix="M">0</h3>
              <p>Constituent cases processed</p>
            </div>
            <div className="stat">
              <h3 data-count="18">0</h3>
              <p>States & territories</p>
            </div>
            <div className="stat">
              <h3 data-count="99.9" data-suffix="%">0</h3>
              <p>Platform uptime</p>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section>
        <div className="wrap">
          <div className="about-commitment reveal">
            <div className="about-commitment-image">
              <img src={heroSigning} alt="Partnership commitment" />
            </div>
            <div className="about-commitment-content">
              <span className="eyebrow">Our Commitment</span>
              <h2>Built for the long haul.</h2>
              <p>
                Government modernization isn't a sprint—it's a marathon. We're here for the entire journey.
              </p>
              <ul className="commitment-list">
                <li>
                  <strong>Dedicated support teams</strong> who understand public-sector workflows and constraints
                </li>
                <li>
                  <strong>Transparent pricing</strong> with no surprise fees or vendor lock-in
                </li>
                <li>
                  <strong>Continuous updates</strong> that keep pace with evolving regulations and security standards
                </li>
                <li>
                  <strong>Training programs</strong> to ensure your team can use Valor effectively from day one
                </li>
                <li>
                  <strong>Open communication</strong> with regular check-ins, roadmap reviews, and feedback sessions
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Careers Callout */}
      <section className="fog-section">
        <div className="wrap">
          <div className="careers-callout reveal">
            <div className="careers-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <h2>Join our mission.</h2>
            <p>
              We're always looking for talented engineers, designers, and public policy experts who want to make government work better for everyone.
            </p>
            <Link to="/contact" className="btn btn-accent">
              View Open Positions <span className="arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section>
        <div className="wrap">
          <div className="cta-band reveal">
            <h2>Ready to modernize with Valor?</h2>
            <p>Let's talk about how we can help your agency deliver better services.</p>
            <div className="hero-ctas">
              <Link to="/contact" className="btn btn-accent">Get in Touch <span className="arrow">→</span></Link>
              <Link to="/valor-os" className="btn btn-outline" style={{background: 'rgba(255,255,255,.15)', borderColor: 'rgba(255,255,255,.3)', color: '#fff'}}>
                Explore Platform <span className="arrow">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default About;
